import { useRouter } from "next/navigation";
import { Children, cloneElement } from "react";

/**
 * RouterLink - A Next.js client component that enhances child elements with client-side routing capabilities
 *
 * This component wraps its children and adds Next.js client-side navigation features:
 * - Intercepts click events to use router.push() instead of full page refreshes
 * - Implements prefetching on hover and focus for improved performance
 * - Preserves any existing event handlers on the child elements
 */
export default ({ href, children }) => {
  const router = useRouter();

  // Prefetch on hover/focus
  const handlePrefetch = () => {
    router.prefetch(href);
  };

  // Handle click routing
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    router.push(href);
  };

  return Children.map(children, (child) => {
    return cloneElement(child, {
      onClick: (e) => {
        child.props.onClick?.(e);
        handleClick(e);
      },
      onMouseEnter: () => {
        child.props.onMouseEnter?.();
        handlePrefetch();
      },
      onFocus: () => {
        child.props.onFocus?.();
        handlePrefetch();
      },
      href: href,
      style: { ...child.props.style },
    });
  });
};
